import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { Cross2Icon, PlusIcon, TrashIcon } from "@radix-ui/react-icons";
import {
  ClassDetail,
  ClassesService,
  ClassPerson,
  ClassUpdate,
  OrganizationResponseForAdmins,
  SessionFrequency,
  WeeklyTime,
} from "client/openapi";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import {
  Dialog,
  DialogAction,
  DialogActions,
  DialogClose,
  DialogContent,
} from "components/Dialog";
import DayInput from "components/DayPicker";
import { errorText } from "components/Forms/formsApi";
import { FIELD_LABEL } from "components/MeetingDialog/Components/MeetingFields";
import "components/MeetingDialog/index.css";
import { CreatableSelect, Select } from "components/Select";
import SimpleSelect, {
  SimpleChoiceSelect,
  SimpleNumericCreatableSelect,
} from "components/Select/simple";
import { Switch } from "components/Switch";
import { plural, seatsLabel } from "./classText";
import { RecentKind } from "client/openapi/models/RecentKind";
import { recentKey, useRecentOptions } from "util/recentPeople";

const TIME_FORMAT = "h:mma";
const UNDERLINE =
  "py-2 px-1 m-0 border-green-300 border-0 border-b-2 bg-transparent text-sm";
const REPEATS = [
  { value: SessionFrequency.EVERY_7_DAYS, label: "Every 7 days" },
  { value: SessionFrequency.EVERY_14_DAYS, label: "Every 14 days" },
  { value: SessionFrequency.EVERY_21_DAYS, label: "Every 21 days" },
  { value: SessionFrequency.EVERY_28_DAYS, label: "Every 28 days" },
];
const TIMES = Array.from({ length: 96 }, (_, i) =>
  moment({ hour: Math.floor(i / 4), minute: (i % 4) * 15 }).format(TIME_FORMAT)
).map((t) => ({ value: t, label: t }));
const LENGTHS = Array.from({ length: 11 }, (_, i) => ({
  value: (i + 1) * 15,
  label: `${(i + 1) * 15} min`,
}));

// The editor works in the browser's zone, like Book Meeting and the calendar.
const zone = () => moment.tz.guess();

type Time = {
  key: number;
  session_id?: number;
  start: string;
  duration: number;
};

type Draft = {
  name: string;
  subjectId?: number;
  tutorIds: number[];
  locationId?: number | null;
  otherLocation: string;
  frequency: SessionFrequency;
  endsOn: string; // "YYYY-MM-DD"
  times: Time[];
  seats: string; // blank: no limit
  description: string;
  open: boolean;
  studentIds: number[]; // a new class only; a saved class enrolls at once
};

let nextKey = 1;

function emptyDraft(): Draft {
  const start = moment
    .tz(zone())
    .add(7, "days")
    .startOf("day")
    .hour(16)
    .toISOString();
  return {
    name: "",
    tutorIds: [],
    otherLocation: "",
    frequency: SessionFrequency.EVERY_7_DAYS,
    endsOn: moment(start).add(9, "weeks").format("YYYY-MM-DD"),
    times: [{ key: nextKey++, start, duration: 60 }],
    seats: "",
    description: "",
    open: false,
    studentIds: [],
  };
}

function draftOf(detail: ClassDetail): Draft {
  return {
    name: detail.name,
    subjectId: detail.subject.id,
    tutorIds: detail.tutors.map((t) => t.id),
    locationId: detail.location_id,
    otherLocation: detail.other_location ?? "",
    frequency: detail.frequency,
    endsOn: detail.last_date,
    times: detail.weekly_times.map((t) => ({
      key: nextKey++,
      session_id: t.session_id,
      start: t.next_start,
      duration: t.duration,
    })),
    seats: detail.capacity ? String(detail.capacity) : "",
    description: detail.description ?? "",
    open: detail.enrollment_open,
    studentIds: [],
  };
}

const sameSet = (a: number[], b: number[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

/**
 * The weekly times as the server takes them. A new one says when and how
 * long; a kept one says only what the admin changed, so one nobody touched
 * never moves (its next session may have begun since the editor opened).
 */
const weeklyTimes = (times: Time[], before: Time[] = []): WeeklyTime[] =>
  times.map(({ session_id, start, duration }) => {
    const was = before.find((t) => t.session_id && t.session_id === session_id);
    if (!was) return { start, duration };
    return {
      session_id,
      // The change is applied as made, from the start the editor showed.
      ...(!moment(start).isSame(was.start) && {
        start,
        loaded_start: was.start,
      }),
      ...(duration !== was.duration && { duration }),
    };
  });

/** What the editor changed, as the server takes it. */
function changes(before: Draft, draft: Draft): ClassUpdate {
  const update: ClassUpdate = {};
  if (draft.name.trim() !== before.name) update.name = draft.name.trim();
  if (draft.subjectId !== before.subjectId)
    update.org_subject_id = draft.subjectId;
  if (!sameSet(draft.tutorIds, before.tutorIds))
    update.tutor_ids = draft.tutorIds;
  if (draft.locationId !== before.locationId && draft.locationId)
    update.location_id = draft.locationId;
  if (draft.otherLocation !== before.otherLocation && draft.otherLocation)
    update.other_location = draft.otherLocation;
  // The last day, in the org's time zone (the server reads it so).
  if (draft.endsOn !== before.endsOn) update.ends_on_or_before = draft.endsOn;
  const sent = weeklyTimes(draft.times, before.times);
  const timesChanged =
    draft.times.length !== before.times.length ||
    sent.some((t) => !t.session_id || t.start || t.duration);
  if (timesChanged) update.weekly_times = sent;
  if (draft.seats !== before.seats)
    update.capacity = draft.seats ? Number(draft.seats) : null;
  if (draft.description !== before.description)
    update.description = draft.description;
  return update;
}

/** Why the class can't be saved yet, if it can't. */
/** Why the class can't be saved yet; the end date is a day in the org's zone. */
function problem(draft: Draft, orgZone: string): string | undefined {
  if (!draft.name.trim()) return "Give the class a name.";
  if (!draft.subjectId) return "Pick a subject.";
  if (
    draft.seats &&
    !(Number.isInteger(Number(draft.seats)) && Number(draft.seats) > 0)
  )
    return "Seats must be a whole number, or blank for no limit.";
  const last = moment.tz(draft.endsOn, "YYYY-MM-DD", orgZone).endOf("day");
  if (draft.times.some((t) => !moment(t.start).isBefore(last)))
    return "Each weekly time must start before the end date.";
  return undefined;
}

/** "Owen Reyes joined 10 sessions." with Undo, until the next change. */
type Undo = { text: string; run?: () => void };

/**
 * A class: its facts and weekly times on the left (Save changes applies
 * them to every session not yet billed), its students on the right
 * (added or removed at once, with Undo). New class opens it empty.
 */
export default function ClassDialog({
  organization,
  classId,
  defaultZone,
  onClose,
  onChanged,
}: {
  organization: OrganizationResponseForAdmins;
  /** Undefined: a new class. */
  classId?: number;
  /** The org's time zone, for a new class's end date. */
  defaultZone?: string;
  onClose: () => void;
  /** Something was saved: the list reloads. */
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<ClassDetail>();
  const [before, setBefore] = useState<Draft>();
  const [draft, setDraft] = useState<Draft | undefined>(() =>
    classId ? undefined : emptyDraft()
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [undo, setUndo] = useState<Undo>();
  const [deleting, setDeleting] = useState(false);
  // One roster change at a time, so a quick second pick isn't sent twice.
  const [changingRoster, setChangingRoster] = useState(false);
  const rosterBusy = useRef(false); // for Undo's older callbacks too
  const undoTimer = useRef<number>();

  useEffect(() => {
    if (!classId) return;
    ClassesService.getClassDetail({ classId })
      .then((loaded) => {
        setDetail(loaded);
        const start = draftOf(loaded);
        setBefore(start);
        setDraft(start);
      })
      .catch((e) => setError(errorText(e, "The class didn't load.")));
  }, [classId]);

  useEffect(() => () => window.clearTimeout(undoTimer.current), []);
  const showUndo = (next?: Undo) => {
    window.clearTimeout(undoTimer.current);
    setUndo(next);
    if (next)
      undoTimer.current = window.setTimeout(() => setUndo(undefined), 8000);
  };

  const tutors = useMemo(
    () =>
      organization.tutors
        .filter((t) => !t.archived_at)
        .sort((a, b) => a.first_name.localeCompare(b.first_name)),
    [organization.tutors]
  );
  const students = useMemo(
    () =>
      organization.students
        .filter((s) => !s.archived_at)
        .sort((a, b) =>
          `${a.first_name} ${a.last_name}`.localeCompare(
            `${b.first_name} ${b.last_name}`
          )
        ),
    [organization.students]
  );
  const subjects = organization.subjects.filter((s) => s.active);
  const places = organization.locations;
  const tutorPicker = useRecentOptions([RecentKind.TUTOR]);
  const studentPicker = useRecentOptions([RecentKind.STUDENT]);

  if (!draft)
    return (
      <Shell title="" onClose={onClose}>
        <p className="py-16 text-center text-gray-600">{error ?? "Loading…"}</p>
      </Shell>
    );

  const set = (change: Partial<Draft>) =>
    setDraft((current) => current && { ...current, ...change });
  const setTime = (key: number, change: Partial<Time>) =>
    set({
      times: draft.times.map((t) => (t.key === key ? { ...t, ...change } : t)),
    });

  const roster: ClassPerson[] = detail
    ? detail.roster
    : students
        .filter((s) => draft.studentIds.includes(s.id))
        .map((s) => ({
          id: s.id,
          first_name: s.first_name,
          last_name: s.last_name,
        }));
  const onRoster = new Set(roster.map((s) => s.id));

  // A saved class: add or remove at once, then offer Undo, which reverses
  // exactly the sessions just changed (`only`).
  const enroll = (studentId: number, name: string, only?: number[]) => {
    if (!detail) return set({ studentIds: [...draft.studentIds, studentId] });
    if (rosterBusy.current) return;
    rosterBusy.current = true;
    setError(undefined);
    setChangingRoster(true);
    ClassesService.enrollClassStudent({
      classId: detail.id,
      requestBody: {
        student_id: studentId,
        ...(only && { meeting_ids: only }),
      },
    })
      .then((result) => {
        setDetail(result.klass);
        onChanged();
        showUndo(
          only
            ? undefined
            : {
                text: `${name} joined ${plural(result.sessions, "session")}.`,
                ...(result.meeting_ids.length && {
                  run: () => unenroll(studentId, name, result.meeting_ids),
                }),
              }
        );
      })
      .catch((e) => setError(errorText(e, `${name} couldn't be added.`)))
      .finally(() => {
        rosterBusy.current = false;
        setChangingRoster(false);
      });
  };
  const unenroll = (studentId: number, name: string, only?: number[]) => {
    if (!detail)
      return set({
        studentIds: draft.studentIds.filter((id) => id !== studentId),
      });
    if (rosterBusy.current) return;
    rosterBusy.current = true;
    setError(undefined);
    setChangingRoster(true);
    ClassesService.unenrollClassStudent({
      classId: detail.id,
      studentId,
      ...(only && { meetingIds: only }),
    })
      .then((result) => {
        setDetail(result.klass);
        onChanged();
        showUndo(
          only
            ? undefined
            : {
                text: `${name} left ${plural(result.sessions, "session")}.`,
                ...(result.meeting_ids.length && {
                  run: () => enroll(studentId, name, result.meeting_ids),
                }),
              }
        );
      })
      .catch((e) => setError(errorText(e, `${name} couldn't be removed.`)))
      .finally(() => {
        rosterBusy.current = false;
        setChangingRoster(false);
      });
  };

  const setOpen = (open: boolean) => {
    if (!detail) return set({ open });
    ClassesService.updateClass({
      classId: detail.id,
      requestBody: { enrollment_open: open },
    })
      .then((saved) => {
        setDetail(saved);
        set({ open });
        onChanged();
      })
      .catch((e) => setError(errorText(e, "Enrollment didn't change.")));
  };

  // The org's zone: the class's, or the org's default for a new class.
  const orgZone = detail?.timezone ?? defaultZone ?? zone();
  const blocked = problem(draft, orgZone);
  const save = () => {
    if (blocked) return setError(blocked);
    setSaving(true);
    setError(undefined);
    const request =
      detail && before
        ? ClassesService.updateClass({
            classId: detail.id,
            requestBody: changes(before, draft),
          })
        : ClassesService.createClass({
            requestBody: {
              org_id: organization.id,
              name: draft.name.trim(),
              org_subject_id: draft.subjectId as number,
              tutor_ids: draft.tutorIds,
              student_ids: draft.studentIds,
              location_id: draft.locationId || undefined,
              other_location: draft.otherLocation || undefined,
              frequency: draft.frequency,
              ends_on_or_before: draft.endsOn,
              weekly_times: weeklyTimes(draft.times),
              capacity: draft.seats ? Number(draft.seats) : undefined,
              description: draft.description || undefined,
              enrollment_open: draft.open,
            },
          });
    request
      .then(() => {
        onChanged();
        onClose();
      })
      .catch((e) => setError(errorText(e, "The class wasn't saved.")))
      .finally(() => setSaving(false));
  };

  const remove = () => {
    if (!detail) return;
    ClassesService.deleteClass({ classId: detail.id })
      .then(() => {
        onChanged();
        onClose();
      })
      .catch((e) => {
        setDeleting(false);
        setError(errorText(e, "The class wasn't deleted."));
      });
  };

  const dirty =
    !detail || !before || Object.keys(changes(before, draft)).length > 0;
  const firstStart = draft.times
    .map((t) => moment(t.start))
    .reduce((a, b) => (b.isBefore(a) ? b : a));
  const place = draft.locationId
    ? places.find((p) => p.id === draft.locationId)
    : undefined;

  return (
    <Shell
      title={detail ? detail.name : "New class"}
      onClose={onClose}
      open={draft.open}
      onOpenChange={setOpen}
    >
      {/* Inside the dialog: a click outside a modal dialog closes it. */}
      {undo && (
        <div
          role="status"
          className="mb-4 flex items-center justify-between gap-6 rounded-md bg-green-600 px-4 py-2.5 text-sm font-medium text-white"
        >
          {undo.text}
          {undo.run && (
            <button
              type="button"
              disabled={changingRoster}
              className="font-bold underline disabled:opacity-50"
              onClick={() => {
                const run = undo.run;
                showUndo(undefined);
                run?.();
              }}
            >
              Undo
            </button>
          )}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        {/* The class and its weekly times. */}
        <div className="meeting-fields flex flex-col gap-4 rounded-lg bg-gray-100 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="min-w-0">
              <label htmlFor="class-name" className={FIELD_LABEL}>
                Name
              </label>
              <input
                id="class-name"
                value={draft.name}
                onChange={(e) => set({ name: e.target.value })}
                placeholder="SAT Math Bootcamp"
                maxLength={150}
                className="h-8 w-full rounded border border-gray-300 px-2 text-[13px] placeholder:text-gray-400"
              />
            </div>
            <div className="min-w-0">
              <div className={FIELD_LABEL}>Subject</div>
              <Select
                id="class-subject"
                classNamePrefix="meeting-field"
                options={subjects.map((s) => ({ value: s.id, label: s.name }))}
                value={subjects
                  .filter((s) => s.id === draft.subjectId)
                  .map((s) => ({ value: s.id, label: s.name }))}
                onChange={(option: { value: number } | null) =>
                  set({ subjectId: option?.value })
                }
                placeholder="Pick a subject"
              />
            </div>
          </div>

          <div className="min-w-0">
            <div className={FIELD_LABEL}>
              Tutors <span className="font-normal text-gray-500">optional</span>
            </div>
            <Select
              id="class-tutors"
              classNamePrefix="meeting-field"
              isMulti
              isClearable={false}
              options={tutorPicker.options(
                tutors.map((t) => ({
                  value: t.id,
                  label: `${t.first_name} ${t.last_name}`,
                })),
                (option) => recentKey(RecentKind.TUTOR, option.value)
              )}
              onInputChange={tutorPicker.onInputChange}
              value={tutors
                .filter((t) => draft.tutorIds.includes(t.id))
                .map((t) => ({
                  value: t.id,
                  label: `${t.first_name} ${t.last_name}`,
                }))}
              onChange={(options: { value: number }[]) =>
                set({ tutorIds: (options ?? []).map((o) => o.value) })
              }
              placeholder="Type a name"
            />
          </div>

          <div className="min-w-0">
            <div className={FIELD_LABEL}>Location</div>
            <CreatableSelect
              id="class-location"
              classNamePrefix="meeting-field"
              placeholder="Select or type a location..."
              options={places.map((p) => ({ value: p.id, label: p.name }))}
              value={
                place
                  ? { value: place.id, label: place.name }
                  : draft.otherLocation
                  ? { value: draft.otherLocation, label: draft.otherLocation }
                  : null
              }
              onChange={(option: any) =>
                set(
                  !option
                    ? { locationId: null, otherLocation: "" }
                    : option.__isNew__
                    ? { locationId: null, otherLocation: option.value }
                    : { locationId: option.value, otherLocation: "" }
                )
              }
              // A saved class's place can change but not go blank: the series
              // edit, like the calendar's, keeps the place when it's empty.
              isClearable={!before?.locationId && !before?.otherLocation}
              isSearchable
              formatCreateLabel={(input: string) =>
                `Use custom location "${input}"`
              }
              components={{ DropdownIndicator: undefined }}
            />
          </div>

          <div className="flex flex-col gap-1">
            {draft.times.map((time, i) => (
              <div
                key={time.key}
                className="flex flex-wrap items-end gap-2 text-sm"
              >
                <div className="mr-2">
                  <p className="text-xs font-bold">
                    {time.session_id ? "Next session" : "First session"}
                  </p>
                  <DayInput
                    id={`class-day-${time.key}`}
                    value={moment.tz(time.start, zone()).toISOString()}
                    classes={UNDERLINE}
                    min={moment().format("YYYY-MM-DD")}
                    setValue={(day: Date) => {
                      const clock = moment
                        .tz(time.start, zone())
                        .format("HH:mm");
                      const date = moment.tz(day, zone()).format("YYYY-MM-DD");
                      setTime(time.key, {
                        start: moment
                          .tz(`${date} ${clock}`, "YYYY-MM-DD HH:mm", zone())
                          .toISOString(),
                      });
                    }}
                    required
                  />
                </div>
                <div className="mr-2">
                  <p className="text-xs font-bold">Time</p>
                  <SimpleSelect
                    id={`class-time-${time.key}`}
                    options={TIMES}
                    value={{
                      value: moment.tz(time.start, zone()).format(TIME_FORMAT),
                      label: moment.tz(time.start, zone()).format(TIME_FORMAT),
                    }}
                    onChange={(option: { value: string }) => {
                      const date = moment
                        .tz(time.start, zone())
                        .format("YYYY-MM-DD");
                      setTime(time.key, {
                        start: moment
                          .tz(
                            `${date} ${option.value}`,
                            "YYYY-MM-DD h:mma",
                            zone()
                          )
                          .toISOString(),
                      });
                    }}
                    required
                  />
                </div>
                <div className="mr-2">
                  <p className="text-xs font-bold">Duration</p>
                  <SimpleNumericCreatableSelect
                    options={LENGTHS}
                    value={{
                      value: time.duration,
                      label: `${time.duration} min`,
                    }}
                    onChange={(option: { value: number }) =>
                      setTime(time.key, { duration: option.value })
                    }
                  />
                </div>
                {draft.times.length > 1 && (
                  <button
                    type="button"
                    aria-label={`Remove weekly time ${i + 1}`}
                    className="mb-2 rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-red-600"
                    onClick={() =>
                      set({
                        times: draft.times.filter((t) => t.key !== time.key),
                      })
                    }
                  >
                    <TrashIcon width={18} height={18} />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="mt-1 flex w-fit items-center gap-1 text-xs font-bold text-green-700 hover:underline"
              onClick={() => {
                const last = draft.times[draft.times.length - 1];
                set({
                  times: [
                    ...draft.times,
                    {
                      key: nextKey++,
                      start: moment(last.start).add(1, "day").toISOString(),
                      duration: last.duration,
                    },
                  ],
                });
              }}
            >
              <PlusIcon /> Add another weekly time
            </button>
          </div>

          <div className="flex flex-wrap items-end gap-2 text-sm">
            <div className="mr-2">
              <p className="text-xs font-bold">Repeats</p>
              <SimpleChoiceSelect
                id="class-repeats"
                options={REPEATS}
                value={REPEATS.find((r) => r.value === draft.frequency)}
                onChange={(option: { value: SessionFrequency }) =>
                  set({ frequency: option.value })
                }
                isDisabled={!!detail}
              />
            </div>
            <div className="mr-2">
              <p className="text-xs font-bold">Ends on or before</p>
              <DayInput
                id="class-ends"
                value={moment
                  .tz(draft.endsOn, "YYYY-MM-DD", zone())
                  .toISOString()}
                classes={UNDERLINE}
                min={`${firstStart
                  .clone()
                  .tz(orgZone)
                  .format("YYYY-MM-DD")}T00:00`}
                setValue={(day: Date) =>
                  set({ endsOn: moment.tz(day, zone()).format("YYYY-MM-DD") })
                }
                required
              />
            </div>
            <div>
              <label htmlFor="class-seats" className="text-xs font-bold">
                Seats
              </label>
              <input
                id="class-seats"
                inputMode="numeric"
                value={draft.seats}
                placeholder="No limit"
                onChange={(e) =>
                  set({ seats: e.target.value.replace(/[^0-9]/g, "") })
                }
                className={`${UNDERLINE} block w-24 focus:ring-0`}
              />
            </div>
          </div>

          <div>
            <label htmlFor="class-description" className={FIELD_LABEL}>
              Description
              <span className="font-normal text-gray-500">
                shown on the public page
              </span>
            </label>
            <textarea
              id="class-description"
              rows={3}
              value={draft.description}
              onChange={(e) => set({ description: e.target.value })}
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-[13px]"
            />
          </div>
        </div>

        {/* The students. */}
        <div className="flex min-w-0 flex-col">
          <div className="flex items-baseline justify-between">
            <h3 className="text-base font-bold">Students</h3>
            <SeatsText
              enrolled={roster.length}
              capacity={detail ? detail.capacity : Number(draft.seats) || null}
            />
          </div>
          <div className="meeting-fields mt-2">
            <Select
              id="class-add-student"
              classNamePrefix="meeting-field"
              options={studentPicker.options(
                students
                  .filter((s) => !onRoster.has(s.id))
                  .map((s) => ({
                    value: s.id,
                    label: `${s.first_name} ${s.last_name}`,
                  })),
                (option) => recentKey(RecentKind.STUDENT, option.value)
              )}
              onInputChange={studentPicker.onInputChange}
              value={null}
              isDisabled={changingRoster}
              onChange={(option: { value: number; label: string } | null) =>
                option && enroll(option.value, option.label)
              }
              placeholder="Add a student"
              noOptionsMessage={() => "No matches"}
            />
          </div>
          {roster.length > 0 ? (
            <ul className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white px-3">
              {roster.map((student) => {
                const name = `${student.first_name} ${student.last_name}`;
                return (
                  <li
                    key={student.id}
                    className="flex items-center justify-between py-2"
                  >
                    <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-[13px] font-medium text-indigo-700">
                      {name}
                    </span>
                    <button
                      type="button"
                      aria-label={`Remove ${name}`}
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600 disabled:opacity-40"
                      disabled={changingRoster}
                      onClick={() => unenroll(student.id, name)}
                    >
                      <Cross2Icon />
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="mt-2 rounded-lg border border-dashed border-gray-300 px-3 py-6 text-center text-sm text-gray-500">
              No students yet.
            </p>
          )}
          <p className="mt-2 text-xs leading-relaxed text-gray-500">
            {detail
              ? "The roster is everyone on any session still to come. Each student is billed per session at their rate for the subject, like any meeting."
              : "They join every session when you save. Each student is billed per session at their rate for the subject, like any meeting."}
          </p>
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-4 text-sm text-red-600">
          {error}
        </p>
      )}

      <div className="mt-5 flex items-center justify-between gap-3">
        <div>
          {detail && (
            <Button
              color={ButtonColor.RED}
              fill={ButtonFill.HOLLOW}
              size={ButtonSize.SMALL}
              onClick={() => setDeleting(true)}
            >
              Delete class
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <DialogClose asChild>
            <Button
              color={ButtonColor.BLACK}
              fill={ButtonFill.HOLLOW}
              size={ButtonSize.SMALL}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            color={ButtonColor.GREEN}
            size={ButtonSize.SMALL}
            disabled={saving || !dirty}
            onClick={save}
          >
            {saving ? "Saving…" : detail ? "Save changes" : "Create class"}
          </Button>
        </div>
      </div>

      <Dialog open={deleting} onOpenChange={setDeleting}>
        {deleting && detail && (
          <DialogContent className="dialog-content">
            <h2 className="mb-2 text-xl font-semibold">
              Delete {detail.name}?
            </h2>
            <p className="text-sm text-gray-700">
              Its {plural(detail.sessions_left, "session")} still to come are
              cancelled. Past sessions stay on the calendar and in billing.
            </p>
            <DialogActions>
              <DialogAction
                primary={true}
                color={ButtonColor.RED}
                onClick={remove}
              >
                Delete
              </DialogAction>
              <DialogClose asChild>
                <DialogAction color={ButtonColor.SKYBLUE}>Cancel</DialogAction>
              </DialogClose>
            </DialogActions>
          </DialogContent>
        )}
      </Dialog>
    </Shell>
  );
}

/** "7 of 12 seats" (amber when over), or "7 students" with no limit. */
function SeatsText({
  enrolled,
  capacity,
}: {
  enrolled: number;
  capacity?: number | null;
}) {
  const over = !!capacity && enrolled > capacity;
  return (
    <span
      className={`text-sm font-bold ${
        over ? "text-amber-600" : "text-green-700"
      }`}
    >
      {seatsLabel(enrolled, capacity)}
    </span>
  );
}

/** The dialog frame: Book Meeting's header band, the title, Open for enrollment. */
function Shell({
  title,
  onClose,
  open,
  onOpenChange,
  children,
}: {
  title: string;
  onClose: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="dialog-content dialog-content--left medium-wide bg-neutral-50 font-montserrat pb-6"
        gradientHeader={true}
        aria-describedby={undefined}
      >
        <div className="-mt-4 mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-3xl font-bold text-neutral-700">{title}</h2>
          {onOpenChange && (
            <div className="flex items-center gap-3">
              <Switch
                name="class-open"
                checked={!!open}
                onCheckedChange={onOpenChange}
              >
                <label
                  htmlFor="class-open"
                  className="mr-2 cursor-pointer text-sm font-bold"
                >
                  Open for enrollment
                </label>
              </Switch>
            </div>
          )}
        </div>
        {children}
      </DialogContent>
    </Dialog>
  );
}
